
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';

// Safely convert any value to a lowercase string
const toSearchString = (val) => {
  if (val === null || val === undefined) return '';
  return String(val).toLowerCase();
};

const SearchableDropdown = ({
  options = [],
  onSelect,
  onInputChnge,
  placeholder = 'Search...',
  selectedValue,
  disabled = false,
  clearable = true,
}) => {
  const dropdownRef = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Find currently selected option
  const selectedItem = useMemo(() => {
    return options.find(
      (option) =>
        String(option.value) === String(selectedValue) ||
        String(option.label) === String(selectedValue)
    );
  }, [options, selectedValue]);

  // Keep selected label visible when dropdown is closed
  useEffect(() => {
    if (!showDropdown) {
      setSearchTerm(selectedItem?.label || '');
    }
  }, [selectedItem, showDropdown]);

  // IMPORTANT: Filter only using searchTerm.
  // Never filter using selectedValue.
  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      toSearchString(option.label).includes(
        toSearchString(searchTerm)
      )
    );
  }, [options, searchTerm]);

  // Open dropdown and show all options
  const handleDropdownOpen = () => {
    if (disabled) return;

    if (!showDropdown) {
      setSearchTerm('');
      setShowDropdown(true);
    }
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearchTerm(value);
    setShowDropdown(true);

    if (onInputChnge) {
      onInputChnge(value);
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    if (disabled) return;

    onSelect(option.value);

    setSearchTerm(option.label);
    setShowDropdown(false);
  };

  // Clear selected value
  const handleClear = (e) => {
    e.stopPropagation();

    onSelect(null);
    setSearchTerm('');
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick
      );
    };
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchTerm(selectedItem?.label || '');
    }

    if (e.key === 'Enter' && showDropdown) {
      e.preventDefault();

      if (filteredOptions.length === 1) {
        handleSelect(filteredOptions[0]);
      }
    }

    if (e.key === 'ArrowDown') {
      setShowDropdown(true);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="searchable-dropdown"
      style={{
        position: 'relative',
        width: '100%',
      }}
    >
      <InputGroup>
        <FormControl
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          disabled={disabled}
          autoComplete="off"
          onFocus={handleDropdownOpen}
          onClick={handleDropdownOpen}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />

        {clearable && selectedItem && !disabled && (
          <InputGroup.Text
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            style={{ cursor: 'pointer' }}
          >
            &times;
          </InputGroup.Text>
        )}

        <InputGroup.Text
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleDropdownOpen}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              transform: showDropdown
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            &#9662;
          </span>
        </InputGroup.Text>
      </InputGroup>

      {showDropdown && !disabled && (
        <div
          className="searchable-dropdown-menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: '#fff',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            marginTop: '2px',
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value ?? index}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                style={{
                  padding: '9px 12px',
                  cursor: 'pointer',
                  backgroundColor:
                    String(option.value) === String(selectedValue)
                      ? '#f0f0f0'
                      : '#fff',
                  borderBottom: '1px solid #f1f1f1',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    String(option.value) === String(selectedValue)
                      ? '#f0f0f0'
                      : '#fff';
                }}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: '10px 12px',
                color: '#999',
                textAlign: 'center',
              }}
            >
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
